/**
 * These styles are technically not part of the basket but are closely related. So keeping it in the basket folder for now.
 */
import styled, { css } from "styled-components";
import { getBreakpoint } from "../../../../themes/common";
import { theme, type ZIndexType } from "../theme/lexusTheme";
import * as Button from "./Button";
import * as NavigationStepStyles from "./NavigationStep";
import * as IconWrapper from "../globals/IconWrapper";

export const NAV_PRIMARY_HEIGHT = 76;
export const NAV_HEIGHT_MOBILE = 66;
type StickyWrapperProps = {
    position?: "fixed" | "relative";
    top?: number;
    zIndexName?: NonNullable<ZIndexType>;
};

export const StickyWrapper = styled.div<StickyWrapperProps>`
    position: fixed;
    top: var(--visibleHeaderHeight);
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100vw;
    height: ${NAV_PRIMARY_HEIGHT}px;
    transition: top 0.5s ease-out; /* Transition of top should match Zaidans implementation */
    z-index: ${theme.zIndex.sticky};

    ${({ position }) =>
        position &&
        css`
            position: ${position};
        `}

    ${({ top }) =>
        top &&
        css`
            top: calc(var(--visibleHeaderHeight) + ${top}px);
        `};

    /* Follows Zaidan breakpoints */
    @media (min-width: 768px) and (max-width: 991px) {
        z-index: ${theme.zIndex.modal};
    }

    /* Override the zindex when the mobile basket is open */
    ${({ zIndexName }) =>
        zIndexName &&
        css`
            z-index: ${theme.zIndex[zIndexName]};
        `};
`;

export const DesktopNav = styled.div`
    display: none;
    background: ${theme.colors.steel};

    @media ${getBreakpoint("up", "lg")} {
        display: block;
    }

    ${NavigationStepStyles.Wrapper} {
        flex-direction: row;
        justify-content: center;
        margin: 0 auto;
        padding: ${theme.space[3]}px 0;

        /* First item links shouldn't have padding as this cause the items to look off-center */

        a:first-child {
            ${NavigationStepStyles.Step} {
                &::before {
                    display: none;
                }
            }
        }
    }
`;

export const MobileNav = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${theme.colors.steel};

    @media ${getBreakpoint("up", "lg")} {
        display: none;
    }

    ${NavigationStepStyles.Wrapper} {
        flex-direction: column;
        padding: 25px 0;

        ${NavigationStepStyles.Step} {
            margin: 10px 0;
            padding-left: 0;
            padding-right: 0;
        }
    }
`;

export const Badge = styled.div`
    float: left;
    margin: 0 ${theme.space[1]}px;
    font-family: ${theme.fonts.book};
    font-size: 1.3rem;
    color: ${theme.colors.white};

    @media ${getBreakpoint("up", "lg")} {
        font-size: 1.4rem;
    }
`;

export const MobileNavHeader = styled.div<{ disabled?: boolean }>`
    position: relative; /* necessary for FloatingNav */
    flex-grow: 1;
    width: 0;
    padding: 16px 0 12px; /* Needed on model / engine and necessary for FloatingNav */
    transition: opacity 0.3s;
    opacity: ${({ disabled }) => (disabled ? 0.75 : 1)};
`;

export const MobileNavButton = styled(Button.Link)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-left: 10px;
    padding-right: 20px;
    text-align: center;
    cursor: ${({ disabled }) => (disabled ? "normal" : "pointer")};

    ${Badge} {
        float: none;
        font-size: 1.2rem;
        line-height: 1.8rem;
        text-align: center;
        color: ${theme.colors.platinumMedium};
    }

    ${NavigationStepStyles.Step} {
        display: block;
        height: auto;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    ${IconWrapper.ToggleIconWrapper} {
        position: absolute;
        top: 50%;
        right: ${theme.space[2]}px;
        transform: translateY(-50%);
    }

    .icon::before {
        top: 3px;
        font-size: 1.6rem;
        color: ${theme.colors.white};
    }

    html[dir="rtl"] & {
        padding-left: 20px;
        padding-right: 10px;
        ${IconWrapper.ToggleIconWrapper} {
            left: ${theme.space[2]}px;
            right: auto;
        }
    }
`;

export const BackButton = styled(Button.Link)`
    display: flex;
    position: absolute;
    top: 50%;
    left: 0;
    align-items: center;
    line-height: 1.8rem;
    transform: translateY(-50%);

    svg {
        color: ${theme.colors.white};
    }

    span {
        display: none;
        margin-left: 10px;
        font-size: 1.3rem;
        color: ${theme.colors.white};

        @media ${getBreakpoint("up", "xl")} {
            display: inline-block;
        }
    }

    html[dir="rtl"] & {
        left: auto;
        right: 0;
        span {
            margin-left: 0;
            margin-right: 10px;
        }
    }
`;

export const StepText = styled.span`
    font-family: ${theme.fonts.book};
    font-size: 1.4rem;
    line-height: 2rem;
    text-overflow: ellipsis;
    color: ${theme.colors.white};
    white-space: nowrap;
    overflow: hidden;

    @media ${getBreakpoint("up", "lg")} {
        font-size: 1.4rem;
    }
`;

export const NavigationDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 0 ${theme.space[3]}px;
    background-color: ${theme.colors.steel};
    border-top: 1px solid ${theme.colors.steel};
    border-radius: 0;
    box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.08);
`;
